/* eslint-disable */
import { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";

/**
 * AdminVenueStripe — Session 29 UI for the Stripe Connect hardening
 * endpoints. Exposes:
 *   • Filterable status overview table (Not invited / Invitation sent / …)
 *   • 7 per-venue actions (Send / Copy / Resend / Continue / Refresh /
 *     Open Stripe dashboard / Disconnect)
 *   • CSV export
 *   • Test-mode $100 verification runner + reconciliation table
 *
 * Every mutation goes through the venue-level endpoints in
 * /app/backend/routes/stripe_connect.py — this file is pure glue.
 */

const STATUS_BADGES = {
  not_connected:   { label: "Not connected",     color: "#8a8a8a" },
  setup_started:   { label: "Setup started",     color: "#c8a24a" },
  action_required: { label: "Action required",   color: "#c46a3a" },
  under_review:    { label: "Under review",      color: "#5a7fb5" },
  ready:           { label: "Ready for payments", color: "#4a8f5a" },
  restricted:      { label: "Restricted",        color: "#b04040" },
};

const FILTERS = [
  { key: "all",             label: "All" },
  { key: "not_connected",   label: "Not invited" },
  { key: "setup_started",   label: "Invitation sent" },
  { key: "action_required", label: "Incomplete" },
  { key: "under_review",    label: "Under review" },
  { key: "ready",           label: "Ready" },
  { key: "restricted",      label: "Restricted" },
];

const BACKEND = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

export default function AdminVenueStripe() {
  const [venues, setVenues] = useState([]);
  const [counts, setCounts] = useState({});
  const [testMode, setTestMode] = useState(false);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});
  const [inviteRow, setInviteRow] = useState(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [testRuns, setTestRuns] = useState(() => {
    try { return JSON.parse(localStorage.getItem("metier_test_checkout_runs") || "[]"); }
    catch { return []; }
  });

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/venues/stripe/overview");
      setVenues(r.data.venues || []);
      setCounts(r.data.counts || {});
      setTestMode(!!r.data.test_mode);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Load failed");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const filtered = filter === "all" ? venues : venues.filter(v => v.badge === filter);

  const withBusy = async (slug, fn) => {
    setBusy(s => ({...s, [slug]: true}));
    try { await fn(); }
    finally { setBusy(s => ({...s, [slug]: false})); }
  };

  const sendSetup = (row, isReminder = false) => {
    if (!inviteEmail || !inviteEmail.includes("@")) {
      toast.error("Enter a valid owner email first");
      return;
    }
    withBusy(row.slug, async () => {
      try {
        const r = await api.post(`/admin/venues/${row.slug}/stripe/invite`, {
          owner_email: inviteEmail,
          reminder: isReminder,
        });
        toast.success(r.data.email_sent ? `Setup email sent to ${inviteEmail}` : "Onboarding link created (email delivery unavailable)");
        setInviteRow(null);
        setInviteEmail("");
        load();
      } catch (e) { toast.error(e?.response?.data?.detail || "Invite failed"); }
    });
  };

  const copySetupLink = (row) => withBusy(row.slug, async () => {
    try {
      const r = await api.post(`/admin/venues/${row.slug}/stripe/onboard`, {});
      await navigator.clipboard.writeText(r.data.onboarding_url);
      toast.success("Setup link copied to clipboard");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to mint link"); }
  });

  const refreshStatus = (row) => withBusy(row.slug, async () => {
    try {
      await api.get(`/admin/venues/${row.slug}/stripe/status?refresh=true`);
      toast.success(`Status refreshed for ${row.venue_name}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Refresh failed"); }
  });

  const disconnect = (row) => {
    if (!window.confirm(`Disconnect Stripe from ${row.venue_name}?\n\nThis removes the account from Métier but does NOT delete the Stripe account itself. The venue can be re-connected later.`)) return;
    withBusy(row.slug, async () => {
      try {
        await api.post(`/admin/venues/${row.slug}/stripe/disconnect`);
        toast.success(`Disconnected ${row.venue_name}`);
        load();
      } catch (e) { toast.error(e?.response?.data?.detail || "Disconnect failed"); }
    });
  };

  const openStripeDashboard = (row) => {
    if (!row.stripe_account_id) { toast.error("No connected account"); return; }
    window.open(`https://dashboard.stripe.com/connect/accounts/${row.stripe_account_id}`, "_blank", "noreferrer");
  };

  const exportCsv = () => {
    const headers = ["Venue","Owner email","Status","Account ID","Charges","Payouts","Details","Requirements due","Invitation sent","Last sync"];
    const rows = filtered.map(v => [
      v.venue_name, v.owner_email || "", v.label, v.stripe_account_id || "",
      v.charges_enabled ? "yes" : "no", v.payouts_enabled ? "yes" : "no",
      v.details_submitted ? "yes" : "no",
      (v.requirements_currently_due || []).join("; "),
      v.invitation_sent_at || "", v.status_synced_at || "",
    ]);
    const csv = [headers, ...rows].map(r =>
      r.map(c => `"${String(c ?? "").replace(/"/g,'""')}"`).join(",")
    ).join("\n");
    const blob = new Blob([csv], {type: "text/csv"});
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `metier-stripe-status-${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const runTestCheckout = async () => {
    if (!testMode) {
      toast.error("Test checkout is disabled while using a live Stripe key");
      return;
    }
    setBusy(s => ({...s, __test: true}));
    try {
      const r = await api.post("/admin/stripe/test-checkout");
      const run = {
        at: new Date().toISOString(),
        session_id: r.data.session_id,
        test_account_id: r.data.test_account_id,
        checkout_url: r.data.checkout_url,
        gross_cents: r.data.expected_gross_cents,
        fee_cents: r.data.expected_application_fee_cents,
        allocation_cents: r.data.expected_venue_allocation_cents,
      };
      const next = [run, ...testRuns].slice(0, 8);
      setTestRuns(next);
      localStorage.setItem("metier_test_checkout_runs", JSON.stringify(next));
      window.open(r.data.checkout_url, "_blank", "noreferrer");
      toast.success("Test checkout created — complete payment with 4242 4242 4242 4242");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test checkout failed");
    } finally { setBusy(s => ({...s, __test: false})); }
  };

  return (
    <div className="m-admin-section" data-testid="admin-venue-stripe">
      <div className="m-card" style={{marginBottom: 16}}>
        <span className="m-eyebrow" style={{color: "var(--gold)"}}>Métier · Stripe Connect</span>
        <h2 className="rec-h2" style={{margin: "6px 0 12px"}}>Venue payment status.</h2>
        <p className="m-muted" style={{maxWidth: 640, marginTop: -4}}>
          Send Stripe setup invitations, monitor onboarding, and reconcile payouts.
          {testMode
            ? <span style={{color:"var(--gold)"}}> · Test mode active — Run test-mode checkout is enabled below.</span>
            : <span> · Live mode — test-mode checkout runner is disabled by design.</span>}
        </p>

        {/* Session 57 · quick link to the live-status console */}
        <div style={{marginTop: 10}}>
          <a
            href="/admin/stripe-status"
            data-testid="link-stripe-live-status"
            style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              padding: "8px 16px", background: "#1B2340", color: "#fff",
              borderRadius: 999, fontSize: 12, fontWeight: 600, textDecoration: "none",
              letterSpacing: "0.04em",
            }}
          >
            Live Stripe Status + Refresh →
          </a>
          <span style={{fontSize: 12, color: "rgba(27,35,64,0.55)", marginLeft: 10}}>
            Use this when Métier still thinks a venue is blocked but Stripe already flipped charges/payouts green.
          </span>
        </div>

        {/* Filter tabs with counts */}
        <div style={{display:"flex", flexWrap:"wrap", gap:8, marginTop:14}}>
          {FILTERS.map(f => {
            const n = f.key === "all" ? venues.length : (counts[f.key] || 0);
            return (
              <button key={f.key}
                className={"m-pill light" + (filter === f.key ? " active" : "")}
                onClick={() => setFilter(f.key)}
                data-testid={`stripe-filter-${f.key}`}
                style={{padding:"6px 12px", fontSize:12}}
              >
                {f.label} · {n}
              </button>
            );
          })}
          <button className="m-btn-ghost small" onClick={load} data-testid="stripe-reload" style={{marginLeft:"auto"}}>Reload</button>
          <button className="m-btn-ghost small" onClick={exportCsv} data-testid="stripe-export-csv">Export CSV</button>
        </div>
      </div>

      {/* Test-mode reconciliation runner */}
      <div className="m-card" style={{marginBottom: 16}}>
        <div style={{display:"flex", justifyContent:"space-between", alignItems:"baseline", flexWrap:"wrap", gap:8}}>
          <div>
            <span className="m-eyebrow" style={{color: "var(--gold)"}}>Test-mode verification</span>
            <h3 className="m-side-title" style={{marginTop:6}}>Run test-mode checkout</h3>
            <p className="m-muted small" style={{marginTop:-4, maxWidth:560}}>
              Creates a test connected account, opens a $100 destination-charge Checkout, and
              records the expected split. Complete payment with Stripe test card <code>4242 4242 4242 4242</code>,
              any future expiry, any CVC.
            </p>
          </div>
          <button
            className="m-btn-gold"
            onClick={runTestCheckout}
            disabled={!testMode || busy.__test}
            data-testid="stripe-run-test-checkout"
            title={testMode ? "Run a $100 test-mode checkout" : "Disabled: live Stripe key detected"}
          >
            {busy.__test ? "Creating…" : "Run test checkout →"}
          </button>
        </div>

        {testRuns.length > 0 && (
          <div style={{marginTop:16, overflowX:"auto"}}>
            <table className="m-admin-table" style={{width:"100%", fontSize:13, borderCollapse:"collapse"}}>
              <thead>
                <tr style={{textAlign:"left", color:"var(--m-muted)", borderBottom:"1px solid rgba(212,168,75,0.15)"}}>
                  <th style={{padding:"8px 6px"}}>When</th>
                  <th style={{padding:"8px 6px"}}>Session</th>
                  <th style={{padding:"8px 6px"}}>Test acct</th>
                  <th style={{padding:"8px 6px", textAlign:"right"}}>Gross</th>
                  <th style={{padding:"8px 6px", textAlign:"right"}}>Métier fee</th>
                  <th style={{padding:"8px 6px", textAlign:"right"}}>Venue allocation</th>
                  <th style={{padding:"8px 6px"}}>Reopen</th>
                </tr>
              </thead>
              <tbody>
                {testRuns.map(r => (
                  <tr key={r.session_id} data-testid={`test-run-${r.session_id}`} style={{borderBottom:"1px solid rgba(212,168,75,0.08)"}}>
                    <td style={{padding:"8px 6px"}}>{new Date(r.at).toLocaleString()}</td>
                    <td style={{padding:"8px 6px", fontFamily:"monospace", fontSize:11}}>{r.session_id.slice(0,18)}…</td>
                    <td style={{padding:"8px 6px", fontFamily:"monospace", fontSize:11}}>{r.test_account_id.slice(0,14)}…</td>
                    <td style={{padding:"8px 6px", textAlign:"right"}}>${(r.gross_cents/100).toFixed(2)}</td>
                    <td style={{padding:"8px 6px", textAlign:"right", color:"var(--gold)"}}>${(r.fee_cents/100).toFixed(2)}</td>
                    <td style={{padding:"8px 6px", textAlign:"right"}}>${(r.allocation_cents/100).toFixed(2)}</td>
                    <td style={{padding:"8px 6px"}}>
                      <a href={r.checkout_url} target="_blank" rel="noreferrer" className="m-btn-ghost small">Open →</a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Venue table */}
      {loading ? (
        <div className="m-loading-inline">Loading venues…</div>
      ) : (
        <div className="m-card">
          {filtered.length === 0 ? (
            <div className="m-empty small">No venues match this filter.</div>
          ) : (
            <div style={{display:"flex", flexDirection:"column", gap:0}}>
              {filtered.map(v => {
                const badge = STATUS_BADGES[v.badge] || STATUS_BADGES.not_connected;
                const isInviting = inviteRow === v.slug;
                const hasAccount = !!v.stripe_account_id;
                return (
                  <div key={v.slug} className="m-admin-row"
                    data-testid={`stripe-venue-row-${v.slug}`}
                    style={{padding:"14px 4px", borderTop:"1px solid rgba(212,168,75,0.08)", display:"flex", flexDirection:"column", gap:8}}>

                    <div style={{display:"flex", justifyContent:"space-between", flexWrap:"wrap", gap:12, alignItems:"baseline"}}>
                      <div style={{flex:"1 1 240px"}}>
                        <div className="m-admin-row-name">
                          {v.venue_name}
                          <span
                            data-testid={`stripe-badge-${v.slug}`}
                            style={{
                              marginLeft:8, padding:"3px 8px", fontSize:10,
                              letterSpacing:"0.14em", textTransform:"uppercase",
                              borderRadius:999, background:badge.color, color:"#fff",
                              fontWeight:600,
                            }}
                          >{badge.label}</span>
                        </div>
                        <div className="m-muted small" style={{marginTop:2}}>
                          {v.city ? `${v.city} · ` : ""}{v.stripe_account_id_masked || "no account"}
                          {v.owner_email && <> · {v.owner_email}</>}
                        </div>
                        <div className="m-muted small" style={{marginTop:2, opacity:0.75}}>
                          Charges: {v.charges_enabled ? "✓" : "—"} ·
                          Payouts: {v.payouts_enabled ? "✓" : "—"} ·
                          Details: {v.details_submitted ? "✓" : "—"}
                          {(v.requirements_currently_due || []).length > 0 && (
                            <> · <span style={{color:"#c46a3a"}}>Needs: {v.requirements_currently_due.slice(0,3).join(", ")}</span></>
                          )}
                        </div>
                      </div>

                      <div style={{display:"flex", gap:6, flexWrap:"wrap"}}>
                        {!isInviting && !hasAccount && (
                          <button className="m-btn-gold small" onClick={() => setInviteRow(v.slug)}
                            data-testid={`stripe-action-send-${v.slug}`}>Send setup</button>
                        )}
                        {!isInviting && hasAccount && !v.charges_enabled && (
                          <button className="m-btn-ghost small" onClick={() => setInviteRow(v.slug)}
                            data-testid={`stripe-action-resend-${v.slug}`}>Resend / Continue</button>
                        )}
                        {hasAccount && (
                          <>
                            <button className="m-btn-ghost small" onClick={() => copySetupLink(v)}
                              disabled={busy[v.slug]}
                              data-testid={`stripe-action-copy-${v.slug}`}>Copy link</button>
                            <button className="m-btn-ghost small" onClick={() => refreshStatus(v)}
                              disabled={busy[v.slug]}
                              data-testid={`stripe-action-refresh-${v.slug}`}>Refresh</button>
                            <button className="m-btn-ghost small" onClick={() => openStripeDashboard(v)}
                              data-testid={`stripe-action-dashboard-${v.slug}`}>Open Stripe ↗</button>
                            <button className="m-btn-ghost small" onClick={() => disconnect(v)}
                              disabled={busy[v.slug]}
                              style={{color:"#b04040"}}
                              data-testid={`stripe-action-disconnect-${v.slug}`}>Disconnect</button>
                          </>
                        )}
                      </div>
                    </div>

                    {isInviting && (
                      <div style={{
                        background:"rgba(184,145,58,0.06)",
                        border:"1px dashed rgba(184,145,58,0.35)",
                        borderRadius:8, padding:"10px 14px",
                        display:"flex", gap:10, alignItems:"end", flexWrap:"wrap",
                      }}>
                        <label className="m-field small" style={{margin:0, flex:"1 1 260px"}}>
                          <span>Owner email</span>
                          <input type="email" required value={inviteEmail}
                            onChange={e => setInviteEmail(e.target.value)}
                            placeholder="owner@venue.com"
                            data-testid={`stripe-invite-email-${v.slug}`} />
                        </label>
                        <button className="m-btn-gold small" disabled={busy[v.slug]}
                          onClick={() => sendSetup(v, hasAccount)}
                          data-testid={`stripe-invite-submit-${v.slug}`}>
                          {busy[v.slug] ? "Sending…" : (hasAccount ? "Send reminder" : "Send setup")}
                        </button>
                        <button className="m-btn-ghost small" onClick={() => { setInviteRow(null); setInviteEmail(""); }}>
                          Cancel
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
